import PropTypes from 'prop-types';
import moment from 'moment';
import { STEPS } from './scheduler';

export const SFEntityID = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

export const NotificationCenter = PropTypes.shape({
  addListener: PropTypes.func.isRequired,
  removeListener: PropTypes.func.isRequired,
  fireEvent: PropTypes.func.isRequired,
});

export const dateInFormat = (dateFormat) => (props, propName, componentName) => {
  const parsedDate = moment(props[propName], dateFormat);
  if (parsedDate.isValid()) {
    return;
  }

  return new Error(`Invalid prop '${propName}' supplied to '${componentName}: `
    + `date '${props[propName]}' is not in the expected format '${dateFormat}'`);
};

export const UndefinedNullType = (props, propName, componentName) => {
  const value = props[propName];
  if (value === null || value === void 0) {
    return;
  }

  return new Error(`Invalid prop '${propName}' supplied to '${componentName}: `
    + `: expected undefined/null, but was ${value}`);
};

const validateCategories = (props, propName, componentName) => {
  const categories = props[propName];
  if ( typeof categories !== "undefined" ) {
    const allCategories = ['serviceCatalog', 'successNetwork'];
    categories.forEach(category => {
      if ( allCategories.indexOf(category) === -1 ) {
        return new Error(`Invalid prop '${propName}' supplied to '${componentName}': `
          + `Must be one of ${allCategories} but got ${category}.`);
      }
    });
  }
}

export const SFService = PropTypes.shape({
  id: SFEntityID.isRequired,
  uuid: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  serviceDescription: PropTypes.string,
  serviceCalendarEnabled: PropTypes.bool,
  serviceCalendarInstruction: PropTypes.string,
  phoneNumber: PropTypes.string,
  email: PropTypes.string,
  url: PropTypes.string,
  hoursOfOperation: PropTypes.string,
  kioskQueueSize: PropTypes.number,
  kioskMaxWaitTime: PropTypes.number,
  kioskId: PropTypes.number,
  displayStudentQueue: PropTypes.bool,
  displaySmartThinkingLink: PropTypes.bool,
  virtualCheckInEnabled: PropTypes.bool,
  serviceCategories: validateCategories,
  virtualCheckInOption: PropTypes.bool
});

export const SFServices = PropTypes.arrayOf(SFService);

export const SFConnection = {
  personId: SFEntityID.isRequired,
  uuid: PropTypes.string.isRequired,
  fullName: PropTypes.string.isRequired,
  biography: PropTypes.string,
  overview: PropTypes.string,
  phoneNumber: PropTypes.string,
  mobilePhone: PropTypes.string,
  videoPhone: PropTypes.string,
  email: PropTypes.string,
  officeHours: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    hoursText: PropTypes.string.isRequired
  })),
  services: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    serviceDisplayName: PropTypes.string
  })),
  roles: PropTypes.array.isRequired,
  hasPhoto: PropTypes.bool,
  onlineSchedulingAvailable: PropTypes.bool,
  title: PropTypes.string
};

export const SFNavigationOption = PropTypes.shape({
  id: PropTypes.string,
  icon: PropTypes.string,
  path: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  suboptions: PropTypes.array
});

export const SFNavigationOptions = PropTypes.arrayOf(SFNavigationOption);

export const SFSlot = PropTypes.shape({
  startDate: PropTypes.string.isRequired,
  timeZone: PropTypes.string,
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: SFEntityID.isRequired,
    name: PropTypes.string.isRequired,
    instructions: PropTypes.string
  })),
  owner: PropTypes.shape({
    id: SFEntityID.isRequired,
    name: PropTypes.string.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    photoAvailable: PropTypes.bool
  }),
  scheduleBlock: PropTypes.shape({
    id: SFEntityID.isRequired,
    title: PropTypes.string.isRequired,
    groupSession: PropTypes.bool.isRequired,
    supplementalInstruction: PropTypes.bool.isRequired,
    instructions: PropTypes.string
  }),
  durationsMinutes: PropTypes.arrayOf(PropTypes.number).isRequired
});

export const SFApptTimeSlot = PropTypes.shape({
  slotStartDate: PropTypes.object.isRequired,
  studentTimeZone: PropTypes.string,
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: SFEntityID.isRequired,
    name: PropTypes.string.isRequired,
    instructions: PropTypes.string
  })),
  owner: PropTypes.shape({
    id: SFEntityID.isRequired,
    name: PropTypes.string.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    photoAvailable: PropTypes.bool,
    officeHours: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
        hoursText: PropTypes.string.isRequired,
        walkInsOnly: PropTypes.bool
    }))
  }),
  scheduleBlock: PropTypes.shape({
    id: SFEntityID.isRequired,
    title: PropTypes.string.isRequired,
    groupSession: PropTypes.bool.isRequired,
    supplementalInstruction: PropTypes.bool.isRequired,
    instructions: PropTypes.string
  }),
  slotDuration: PropTypes.number.isRequired,
  hasRecurringReason: PropTypes.bool
});

export const SFSlots = PropTypes.arrayOf(SFSlot);

export const SFApptTimeSlots = PropTypes.arrayOf(SFApptTimeSlot);

export const validSteps = Object.keys(STEPS);

const starfishServiceIdsToMediaTypes = {
  'appointments': 'application/vnd.starfish.appointments.v1+json',
  'successnetwork': 'application/vnd.starfish.successnetwork.v1+json',
  'interventionsinventory': 'application/vnd.starfish.interventionsinventory.v1+json',
};

export const starfishMediaType = (apiServiceType) => starfishServiceIdsToMediaTypes[apiServiceType];

export const SFNotification = PropTypes.shape({
  render: PropTypes.func.isRequired,
  error: PropTypes.func.isRequired,
  success: PropTypes.func.isRequired,
  dismiss: PropTypes.func.isRequired
});

export const SFSurveysTenantLanguage = PropTypes.shape({
  singular: PropTypes.shape({
    progressSurvey: PropTypes.string.isRequired,
    survey: PropTypes.string.isRequired
  }),
  plural: PropTypes.shape({
    progressSurveys: PropTypes.string.isRequired,
    surveys: PropTypes.string.isRequired
  })
});

export const SFUpcomingAppt = {
  startDate: PropTypes.string.isRequired,
  recurrenceEndDate: PropTypes.string,
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: SFEntityID.isRequired,
    name: PropTypes.string.isRequired,
    instructions: PropTypes.string
  })),
  owner: PropTypes.shape({
    id: SFEntityID.isRequired,
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    photoAvailable: PropTypes.bool
  }),
  scheduleBlock: PropTypes.shape({
    id: SFEntityID.isRequired,
    title: PropTypes.string.isRequired,
    groupSession: PropTypes.bool.isRequired,
    recurrenceLabel: PropTypes.string,
    instructions: PropTypes.string
  }),
  duration: PropTypes.number.isRequired,
  isRecurring: PropTypes.bool,
  reason: PropTypes.string,
  description: PropTypes.string,
  courseContext: PropTypes.shape({
    id: SFEntityID.isRequired,
    name: PropTypes.string.isRequired
  })
};

export const StudentListStudent = PropTypes.shape({
  id: SFEntityID.isRequired,
  studentId: PropTypes.string.isRequired,
  fullName: PropTypes.string.isRequired,
  studentType: PropTypes.oneOf(['student', 'prospect']).isRequired,
  email: PropTypes.string.isRequired,
  preferredEmail: PropTypes.string,
  correspondenceTo: PropTypes.string,
  phone: PropTypes.string,
  cellphone: PropTypes.string,
  successScore: PropTypes.number
});

export const ProgressSurveyAnswer = PropTypes.shape({
  studentId: PropTypes.string.isRequired,
  hasPhoto: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  id: SFEntityID.isRequired,
  username: PropTypes.string.isRequired
});

export const ProgressSurveyQuestion = PropTypes.shape({
  id: SFEntityID.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  type: PropTypes.string.isRequired,
  category: PropTypes.string,
  studentView: PropTypes.bool.isRequired,
  commentRequired: PropTypes.bool.isRequired,
  includeDefaultComments: PropTypes.bool.isRequired,
  defaultRaiseComments: PropTypes.string
});

export const InterventionListIntervention = PropTypes.shape({
  interventionId: SFEntityID.isRequired,
  hasPhoto: PropTypes.bool,
  name: PropTypes.string.isRequired,
  studentExtId: PropTypes.string.isRequired,
  studentId: SFEntityID.isRequired,
  studentName: PropTypes.string.isRequired,
  successScore: PropTypes.number,
  type: PropTypes.string.isRequired,
  cleared: PropTypes.string,
  clearedDate: PropTypes.string,
  trigger: PropTypes.string,
  raisedDate: PropTypes.string.isRequired,
  raisedBy: PropTypes.string.isRequired,
  assignedDate: PropTypes.string,
  dueDate: PropTypes.string
});

export const Intervention = PropTypes.shape({
  interventionId: PropTypes.number.isRequired,
  shortTitle: PropTypes.string.isRequired,
  description: PropTypes.string,
  academicCycles: PropTypes.arrayOf(PropTypes.shape({
    academicCycleId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    definition: PropTypes.string,
    displayOrder: PropTypes.number.isRequired
  })),
});

export const TableHeaderCell = PropTypes.shape({
    content: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    sortValue: PropTypes.string
});

export const ScoreDefinition = PropTypes.shape({
  scoreDefinitionId: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  weight: PropTypes.number.isRequired,
  scoreType: PropTypes.string.isRequired,
  scoreDefinitionCategory: PropTypes.string.isRequired,
  predictor: PropTypes.shape({
    predictorId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    definition: PropTypes.string.isRequired,
    displayOrder: PropTypes.number.isRequired,
    interventions: PropTypes.arrayOf(Intervention),
  })
});

export const Occupation = {
  title: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  wage: PropTypes.number.isRequired,
  growth: PropTypes.number.isRequired,
  jobs: PropTypes.number.isRequired,
  matchscore: PropTypes.number
};

export const Quality = {
  name: PropTypes.string.isRequired,
  iconURL: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

export const OfficeHours = PropTypes.arrayOf(PropTypes.shape({
  title: PropTypes.string.isRequired,
  hoursText: PropTypes.string.isRequired,
  walkInsOnly: PropTypes.bool
}));


export const Surveys = PropTypes.arrayOf(PropTypes.shape({
    surveyId: PropTypes.number.isRequired,
    staffTimeZone: PropTypes.string,
    surveyInstructions: PropTypes.string,
    surveyName: PropTypes.string.isRequired,
    dueDate: PropTypes.string,
    formattedDueDate: PropTypes.string,
    formattedUpdatedDate: PropTypes.string,
    updatedDate: PropTypes.string
}));

export const KioskCustomLanguage = PropTypes.shape({
  swipePageHeaderOne: PropTypes.string,
  swipePageHeaderTwo: PropTypes.string,
  swipePageInstructions: PropTypes.string,
  kioskInitiatorPageHeaderOne: PropTypes.string,
  kioskInitiatorPageHeaderTwo: PropTypes.string,
  visitReasonPageHeaderOne: PropTypes.string,
  visitReasonPageHeaderTwo: PropTypes.string,
  prospectiveStudentsInstructions: PropTypes.string,
  prospectiveStudentsPageHeaderTwo: PropTypes.string,
  addedToKioskConfirmationPageHeaderTwo: PropTypes.string,
  relatedToCoursesPageHeaderOne: PropTypes.string,
  relatedToCoursesPageHeaderTwo: PropTypes.string,
  studentFeedbackPageHeaderOne: PropTypes.string,
  studentFeedbackPageHeaderTwo: PropTypes.string,
  groupSessionJoinPageHeaderOne: PropTypes.string,
  groupSessionJoinPageHeaderTwo: PropTypes.string,
  selectMeetingTypePageInstructions: PropTypes.string,
  appointmentsOnlyPageHeaderOne: PropTypes.string,
  appointmentsOnlyPageHeaderTwo: PropTypes.string,
  stillCheckedInPageHeaderOne: PropTypes.string,
  stillCheckedInPageHeaderTwo: PropTypes.string,
  checkoutPageHeaderTwo: PropTypes.string,
  checkIntoAnotherQueuePageHeaderOne: PropTypes.string,
  checkIntoAnotherQueuePageInstructions: PropTypes.string,
  checkoutConfirmationPageHeaderOne: PropTypes.string,
  checkoutConfirmationPageHeaderTwo: PropTypes.string,
  existingProspectiveStudentPageHeaderOne: PropTypes.string,
  existingProspectiveStudentPageHeaderTwo: PropTypes.string,
  existingProspectiveStudentPageInstructions: PropTypes.string,
});

export const IntakePermissions = PropTypes.shape({
  viewingAsStudent: PropTypes.bool.isRequired,
  isHopeCenterIntakeEnabled: PropTypes.bool.isRequired
});

export const IntakeElement = PropTypes.shape({
  id: PropTypes.number.isRequired,
  retired: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  kind: PropTypes.string.isRequired,
  displayType: PropTypes.string,
  questionId: PropTypes.number,
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  text: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    optionalText: PropTypes.bool.isRequired
  }))
});

export const IntakeFormData = PropTypes.shape({
  success: PropTypes.bool.isRequired,
  intakeForm: PropTypes.shape({
    id: PropTypes.number.isRequired,
    hasAnswers: PropTypes.bool.isRequired,
    updatedDate: PropTypes.string,
    elements: PropTypes.arrayOf(IntakeElement)
  })
});

export const UserProfile = PropTypes.shape({
  cellPhone: PropTypes.string,
  correspondenceTo: PropTypes.oneOf(['BOTH_MAIL', 'SCHOOL_MAIL']),
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  phone: PropTypes.string,
  preferredEmail: PropTypes.string,
  pronouns: PropTypes.string,
  reminderTime: PropTypes.string,
  reminderWhen: PropTypes.string,
  schoolEmail: PropTypes.string,
  sendAlert: PropTypes.bool,
  sendAlertTime: PropTypes.number,
  sendReminder: PropTypes.bool,
  textNotificationEnabled: PropTypes.bool,
  timeZone: PropTypes.string,
  userName: PropTypes.string,
  uuid: PropTypes.string,
  videoPhone: PropTypes.string,
  weekendRemindersOff: PropTypes.bool
});

export const StaffProfilePrivileges = PropTypes.shape({
  allowPhotoUpload: PropTypes.bool, 
  allowPreferredEmail: PropTypes.bool,
  calendarLockingEnabled: PropTypes.bool,
  canAssignTrackingItems: PropTypes.bool,
  canEditPronouns: PropTypes.bool,
  canShareLinks: PropTypes.bool,
  exchangeIntegrationEnabled: PropTypes.bool,
  exchangeImpersonationDisclaimer: PropTypes.string,
  googleIntegrationEnabled: PropTypes.bool,
  hasConnect: PropTypes.bool,
  hasEarlyAlert: PropTypes.bool,
  hasStudentRole: PropTypes.bool,
  helpDeskInformation: PropTypes.string,
  isCalendarManager: PropTypes.bool,
  shouldSeeAttendanceTracking: PropTypes.bool,
  shouldSeeZoomIn: PropTypes.bool,
  shouldSeeInterventions: PropTypes.bool,
  shouldSeeSurveys: PropTypes.bool,
  showAppointmentPreferencesTab: PropTypes.bool,
  showExchangeImpersonationSection: PropTypes.bool,
  showMobileEmailInfo: PropTypes.bool,
  showPronouns: PropTypes.bool,
  textMessagingEnabled: PropTypes.bool,
  weekendReminderEmailSettingEnabled: PropTypes.bool
});

export const StaffProfileData = PropTypes.shape({
  appointmentLinkAvailable: PropTypes.bool,
  biography: PropTypes.string,
  cellPhone: PropTypes.string,
  correspondenceTo: PropTypes.oneOf(['BOTH_MAIL', 'SCHOOL_MAIL', 'PREFERRED_MAIL', '']),
  firstName: PropTypes.string,
  generalOverview: PropTypes.string,
  hasPhoto: PropTypes.bool,
  homePage: PropTypes.string,
  lastName: PropTypes.string,
  phone: PropTypes.string,
  preferredEmail: PropTypes.string,
  profileLinkAvailable: PropTypes.bool,
  pronouns: PropTypes.string,
  schoolEmail: PropTypes.string,
  timeZone: PropTypes.string,
  title: PropTypes.string,
  userName: PropTypes.string,
  uuid: PropTypes.string,
  videoPhone: PropTypes.string
});

export const StaffNotificationPreferences = PropTypes.shape({
  alertPreviousTime: PropTypes.number,
  appAlertFlag: PropTypes.bool,
  dailyNotification: PropTypes.bool,
  dailyNotificationTime: PropTypes.string,
  exchangeFreeBusyEnabled: PropTypes.bool,
  exchangeImpersonationEnabled: PropTypes.bool,
  exchangeImpersonationStatus: PropTypes.string,
  flagAssigned: PropTypes.bool,
  flagCleared: PropTypes.bool,
  flagRaised: PropTypes.bool,
  mailNewApp: PropTypes.bool,
  mailNewBlock: PropTypes.bool,
  planningReminderTime: PropTypes.string,
  planningReminderType: PropTypes.string,
  planningReminderWhen: PropTypes.string,
  textNotificationEnabled: PropTypes.bool,
  weekendRemindersOff: PropTypes.bool,
  weeklyNotification: PropTypes.bool,
  weeklyNotificationDay: PropTypes.string,
  weeklyNotificationTime: PropTypes.string
});

export const StaffAppointmentPreferences = PropTypes.shape({
  schedulingType: PropTypes.string,
  minAppTime: PropTypes.number,
  deadLineType: PropTypes.string,
  deadLineValue: PropTypes.object,
});

/**
 * Kiosk Prop Types
 */
// First two configs can be removed when we remove ActiveX, others are used for both ActiveX and Keyboard Emulation
export const SwipeCardConfigs = PropTypes.shape({
  waitingRoomSwipeCardEnabled: PropTypes.bool.isRequired,
  useNonActiveXMethod: PropTypes.bool,
  swipeCardActiveXControlUrl: PropTypes.string,
  waitingRoomSwipeCardParams: PropTypes.string,
  waitingRoomSwipeCardUsbParams: PropTypes.string,
  waitingRoomSwipeCardPattern: PropTypes.instanceOf(RegExp),
  waitingRoomSwipeCardPrefix: PropTypes.string
});

export const VirtualSignInOptions = PropTypes.shape({
  virtualSignInEnabled: PropTypes.bool.isRequired,
  virtualStudentId: SFEntityID,
  virtualStudentLastName: PropTypes.string,
  virtualServiceId: SFEntityID
});

export const KioskService = PropTypes.shape({
  serviceId: SFEntityID,
  serviceUid: PropTypes.string
});

export const AppointmentInstructor = PropTypes.shape({
  name: PropTypes.string,
  uid: PropTypes.string,
  title: PropTypes.string
});

export const KioskAppointmentReason = PropTypes.shape({
  id: SFEntityID
});

export const KioskCourseOption = PropTypes.shape({
  id: SFEntityID
});

export const KioskGroupSession = PropTypes.shape({
  ownerPartyId: SFEntityID,
  id: SFEntityID,
  courseRestrictions: PropTypes.arrayOf(PropTypes.object),
  supplementalInstruction: PropTypes.bool,
  groupSessionSupplementalInstructionRestriction: PropTypes.string
});

export const HelpDeskInformation = PropTypes.shape({
  email: PropTypes.string,
  headerText: PropTypes.string,
  phone: PropTypes.string,
  productHelpName: PropTypes.string,
  sectionTitle: PropTypes.string,
  showToStaff: PropTypes.bool,
  showToStudents: PropTypes.bool,
  studentDisplayText: PropTypes.string,
  supportWebsiteDisplayText: PropTypes.string,
  url: PropTypes.string
});


export const SFVirtualCheckInDaySchedule = PropTypes.shape({
  dayOfWeek: PropTypes.string.isRequired,
  error: PropTypes.string,
  timeSlots: PropTypes.arrayOf(
      PropTypes.shape({
        openTime: PropTypes.string,
        closeTime: PropTypes.string,
        openTimeError: PropTypes.string,
        closeTimeError: PropTypes.string,
        overlapError: PropTypes.string
      })
  )
});

export const SFVirtualCheckInModifyTimeSlot = PropTypes.shape({
  removeTimeSlot: PropTypes.func.isRequired,
  addTimeSlot: PropTypes.func.isRequired,
  setOpenTime: PropTypes.func.isRequired,
  setCloseTime: PropTypes.func.isRequired,
  setDayOfWeek: PropTypes.func.isRequired
});

export const SFVirtualCheckInModifySchedule = PropTypes.shape({
  addDayRow: PropTypes.func.isRequired,
  clearEmptyRows: PropTypes.func.isRequired,
  copyDayRow: PropTypes.func.isRequired,
  removeAllRows: PropTypes.func.isRequired,
  removeDayRow: PropTypes.func.isRequired,
  timeSlotCallBacks: SFVirtualCheckInModifyTimeSlot.isRequired,
  validate: PropTypes.func.isRequired
});

export const RequestHelp = PropTypes.shape({
  studentHelpText: PropTypes.string.isRequired,
  requestHelpEnabled: PropTypes.bool
});

export const StudentSurveyElement = PropTypes.shape({
  id: PropTypes.number.isRequired,
  elementType: PropTypes.string.isRequired,
  elementText: PropTypes.string,
  templateQuestion: PropTypes.shape({
    questionId: PropTypes.number.isRequired,
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.string.isRequired,
    questionOptions: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string.isRequired,
      position: PropTypes.number.isRequired
    })).isRequired,
    required: PropTypes.bool.isRequired
  }),
  elementPosition: PropTypes.number.isRequired
});
